<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '../../../types/site'

defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

/**
 * Must stay in sync with GenericBlocks::EMBED_URL_PATTERN in
 * zth/site-builder-php and with each app's CSP `frame-src`. Adding a provider
 * is deliberately a three-file change.
 */
const PROVIDERS = [
  { label: t('site.f.embed.opt_calendly', 'Calendly'), value: 'calendly', hint: 'https://calendly.com/…' },
  { label: t('site.f.embed.opt_google_maps', 'Google Maps'), value: 'google_maps', hint: 'https://www.google.com/maps/embed?…' },
  { label: t('site.f.embed.opt_google_forms', 'Google Forms'), value: 'google_form', hint: 'https://docs.google.com/forms/…' },
  { label: t('site.f.embed.opt_typeform', 'Typeform'), value: 'typeform', hint: 'https://form.typeform.com/to/…' },
  { label: t('site.f.embed.opt_tally', 'Tally'), value: 'tally', hint: 'https://tally.so/embed/…' },
  { label: t('site.f.embed.opt_youtube', 'YouTube'), value: 'youtube', hint: 'https://www.youtube.com/embed/…' },
  { label: t('site.f.embed.opt_vimeo', 'Vimeo'), value: 'vimeo', hint: 'https://player.vimeo.com/video/…' },
  { label: t('site.f.embed.opt_spotify', 'Spotify'), value: 'spotify', hint: 'https://open.spotify.com/embed/…' }
]

const hintFor = (value?: string) => PROVIDERS.find(p => p.value === value)?.hint ?? ''
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.embed.provider', 'Provider')"
    :help="t('site.f.embed.only_these_services_can_be_embedded_othe_help', 'Only these services can be embedded. Others are blocked by the site\'s security policy.')"
  >
    <USelect
      v-model="block.data.provider"
      :items="PROVIDERS"
      class="w-full"
    />
  </UFormField>

  <UFormField
    :label="t('site.f.embed.embed_url', 'Embed URL')"
    :help="block.data.provider
      ? `${t('site.f.embed.help_prefix', 'Use the provider\'s embed URL, e.g.')} ${hintFor(block.data.provider)} ${t('site.f.embed.help_suffix', '— a normal share link usually will not load. The embedded service sees your visitors\' IP address.')}`
      : t('site.f.embed.help_no_provider', 'Pick a provider first.')"
    :error="!block.data.url ? t('site.f.embed.err_url', 'This block needs an embed URL before the page can be saved.') : undefined"
  >
    <UInput
      v-model="block.data.url"
      :placeholder="t('site.f.common.url_placeholder', 'https://...')"
      class="w-full"
    />
  </UFormField>

  <SiteEditorFieldGroup :label="t('site.f.common.advanced_label', 'Advanced')">
    <UFormField
      :label="t('site.f.common.height_px_label', 'Height (px)')"
      :help="t('site.f.embed.between_200_and_2000_help', 'Between 200 and 2000.')"
    >
      <UInput
        :model-value="block.data.height ?? 600"
        type="number"
        :min="200"
        :max="2000"
        class="w-full"
        @update:model-value="block.data.height = Number($event)"
      />
    </UFormField>

    <UFormField :label="t('site.f.embed.caption', 'Caption (optional)')">
      <UInput
        :model-value="block.data.caption ?? ''"
        class="w-full"
        @update:model-value="block.data.caption = String($event)"
      />
    </UFormField>
  </SiteEditorFieldGroup>
</template>